import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import {
  Wallet,
  LogOut,
  User,
  ChevronDown,
  Copy,
  Shield,
  Plus,
  LayoutDashboard,
  MessageSquare,
  Trophy,
  Bot,
  Compass,
  Eye,
  Globe,
  TrendingUp,
  Activity,
  Zap,
  Droplets,
} from "lucide-react";

const STXOSHI_LOGO = process.env.REACT_APP_LOGO_URL;
const ADMIN_ADDRESSES = [
  'SP3VVPEFZNN84EGMD0GN4XWAZ4X5ZMWC852NGWVWN',
  'SP17175BTCCNHF06Z7TNP3PG9FET3JRFQ85C31WSY',
  'SP1WST899N8B2VR76DCHQ56ZQ78R9VSYT2B2RNTJY',
  'SP2R3H4VQEHPAV8VM8KS0JR8BAS8HTANZ92R64H8K',
];

export default function Header({ showLogo = true }) {
  const navigate = useNavigate();
  const { isConnected, user, addresses, connectWallet, disconnectWallet } = useWallet();
  const { showUsd, toggleUsd } = useCurrency();
  const [dynamicAdmin, setDynamicAdmin] = useState(false);
  useEffect(() => {
    let alive = true;
    setDynamicAdmin(false);
    const addr = addresses?.stx;
    if (isConnected && addr) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/is-admin`, {
        headers: { "X-STX-Address": addr },
      })
        .then((r) => (r.ok ? r.json() : { is_admin: false }))
        .then((d) => { if (alive) setDynamicAdmin(!!d.is_admin); })
        .catch(() => { if (alive) setDynamicAdmin(false); });
    }
    return () => { alive = false; };
  }, [isConnected, addresses?.stx]);
  const isAdmin = isConnected && (ADMIN_ADDRESSES.includes(addresses?.stx) || dynamicAdmin);

  return (
    <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        {showLogo && (
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            {STXOSHI_LOGO ? (
              <img src={STXOSHI_LOGO} alt="STXOSHI" className="h-8 w-8 rounded-full" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">S</span>
              </div>
            )}
            <span className="font-bold text-lg hidden sm:inline">STXOSHI</span>
          </div>
        )}

        {/* Right side - Currency toggle + Connect/Profile */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={toggleUsd}
            className="text-xs px-2.5 py-1.5 rounded-full border border-border text-muted-foreground hover:bg-muted/50 transition-colors font-mono"
            data-testid="global-usd-toggle"
            title="Show values in USD or STXOSHI"
          >
            {showUsd ? "USD" : "STXOSHI"}
          </button>
          {isConnected ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  size="sm"
                  className="text-xs md:text-sm"
                  data-testid="header-wallet-dropdown"
                >
                  <Avatar className="w-5 h-5 mr-1 md:mr-2">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="text-xs bg-primary/20 text-primary">
                      {user?.username?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline">{addresses.stx?.slice(0, 6)}...</span>
                  <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-sm font-medium">
                  {user?.username || 'My Wallet'}
                </div>
                <div className="px-2 py-1 text-xs text-muted-foreground font-mono">
                  {addresses.stx?.slice(0, 12)}...{addresses.stx?.slice(-8)}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => {
                    navigator.clipboard.writeText(addresses.stx);
                    toast.success('Address copied!');
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Address
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/profile/${user?.id}`)}>
                  <User className="w-4 h-4 mr-2" />
                  Profile - User Wallet
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/create')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Launch Your Agents & Bots
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Explore Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/perps')}
                  className="text-orange-400 focus:text-orange-400"
                  data-testid="header-menu-perps"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Perp Trading
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/flip-market')}
                  data-testid="header-menu-flip-market"
                >
                  <Zap className="w-4 h-4 mr-2 text-yellow-400" />
                  Flip Prediction Market
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/lp-flips')}
                  data-testid="header-menu-lp-flips"
                >
                  <Droplets className="w-4 h-4 mr-2 text-cyan-400" />
                  Flip Liquidity Provider
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/pulse')}
                  data-testid="header-menu-pulse"
                >
                  <Activity className="w-4 h-4 mr-2" />
                  STXOSHI Pulse
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/community')}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Community
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/leaderboard')}>
                  <Trophy className="w-4 h-4 mr-2" />
                  Leaderboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/whale-leaderboard')}>
                  <Eye className="w-4 h-4 mr-2 text-purple-400" />
                  Whale Leaderboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/agent-directory')}>
                  <Globe className="w-4 h-4 mr-2 text-green-400" />
                  A2A Agent Directory
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                  <Bot className="w-4 h-4 mr-2" />
                  Your Agents & Bots
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/explore')}>
                  <Compass className="w-4 h-4 mr-2" />
                  Explore All Agents & Bots
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin/withdrawals')}
                      className="text-amber-500 focus:text-amber-500"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={disconnectWallet}
                  className="text-red-500 focus:text-red-500"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Disconnect Wallet
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              onClick={connectWallet}
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Connect Wallet
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
